import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'
import { STACK_GROUPS_TR } from '../../stack'
import { COUNTRIES_TR } from '../../countries'

/**
 * Ana sayfa — Türkçe.
 *
 * Yazıldı, çevrilmedi. Türkiye Digitopia'nın ana pazarı; İngilizce cümlelerin
 * birebir karşılığı burada hem uzun hem yabancı duruyor. Bölüm sırası, iddialar
 * ve sayılar İngilizcesiyle aynı — değişen tek şey nasıl söylendiği.
 *
 * Bağlantılar `../links` üzerinden `/en/…` olarak geliyor ve okunduğu dile
 * `localizeHref()` ile çevriliyor (`src/i18n/href.ts`). Yolları burada tekrar
 * yazmak, sayfa taşındığında altı dosyada taşımak demek olurdu.
 *
 * İngilizce sayfadaki amber CHECK notları burada tekrarlanmadı: aynı iddianın
 * aynı sorusu, ve iki kopya iki kez kapatılır.
 *
 * Terminoloji: "agentic" için altı Türkçe sayfada "ajan tabanlı" kullanılıyor,
 * "ajanik" değil (01.09.2026 kararı).
 */
export const home: WrittenPage = {
  navLabel: 'Ana Sayfa',
  title: '',
  description:
    'Strateji danışmanlığı, dönüşüm yönetimi ve kurumsal yapay zeka çözümleri. Digitopia ajan tabanlı yapay zekayı hayata geçirir; elinizde pilot değil, sonuç kalır.',
  sections: [
    {
      kind: 'hero',
      eyebrow: '#ScaleValue',
      // Same break as English: ikinci cümle kendi satırında başlar.
      headline: 'Yapay zekaya yönetim kurullarında onay çıktı.\nUygulamaya geçenlerin sayısı hâlâ çok az.',
      sub: 'Digitopia, ileri görüşlü şirketlerin iş sonuçlarını en üst düzeye çıkarmak için ajan tabanlı yapay zeka çözümlerini Forward Deployed Engineer ekipleri ve yapay zeka ajanlarıyla hayata geçirir.',
      micro: 'Keşfet. Hayata Geçir. Sonuç Al.',
      photo: {
        src: '/photo/team-applause.jpg',
        alt: 'Bir toplantı masasının etrafında, değerlendirme sonunda alkışlayan bir ekip; arkalarındaki beyaz tahtada üzerinde çalıştıkları plan duruyor.',
      },
      ctas: [
        { label: 'Bize Ulaşın', href: '#contact' },
        { label: 'Yapay zeka hazırlığınızı ölçün', href: LINKS.readinessTool },
      ],
    },
    {
      kind: 'stack',
      groups: STACK_GROUPS_TR,
    },
    {
      kind: 'logos',
      heading: 'Başarılı şirketler Digitopia ile çalışıyor.',
      sub: '300+ müşteri. 40+ ülke. 20+ sektör. 3.000+ ölçüm.',
      cta: { label: 'Başarı hikâyelerini görün', href: LINKS.successStories },
      items: [
        { name: 'Siemens', src: '/logo/siemens.webp' },
        { name: 'Unilever', src: '/logo/unilever.webp' },
        { name: 'Vodafone', src: '/logo/vodafone.webp' },
        // İngilizce sayfayla aynı: 300x300 şeffaf kare. `a101.webp` 30.08.2026'dan
        // beri düzgün bir kırpım ama gri tonlamalı; bu bant hâlâ ağırlıkla renkli
        // olduğu için setin kalanı gelene kadar kare kalıyor.
        { name: 'A101', src: '/logo/a101card.png', padded: true },
        // İngilizce sayfayla aynı: bu dört marka gri tonlamalı 150x100 kırpım
        // yerine renkli `*card.png` karesinden geliyor. `padded` boyutu veriyor —
        // her iki dosya da markanın ortasında durduğu bir tuval, yükseklik sınırı
        // markayı değil tuvali ölçüyor.
        { name: 'Bridgestone', src: '/logo/bridgestonecard.png', padded: true },
        // Bu üçünün renkli dosyası yok; elimizde yalnızca gri kırpım var. Aynı
        // nedenle `padded`: boşluk değil, marka ölçülsün.
        { name: 'DHL', src: '/logo/dhl.webp', padded: true },
        { name: 'Carlsberg Group', src: '/logo/carlsberggroup.webp', padded: true },
        { name: 'Anadolu Efes', src: '/logo/anadoluefes.webp', padded: true },
        { name: 'Garanti BBVA', src: '/logo/garantibbva.webp' },
        { name: 'ING', src: '/logo/ing.webp' },
        { name: 'Brisa', src: '/logo/brisacard.png', padded: true },
        { name: 'TEMSA', src: '/logo/temsacard.png', padded: true },
        { name: 'Petrol Ofisi', src: '/logo/petrolofisicard.png', padded: true },
        { name: 'Turkish Airlines', src: '/logo/turkishairlines.webp' },
        { name: 'LC Waikiki', src: '/logo/lcwaikikicard.png', padded: true },
        { name: 'Enerjisa', src: '/logo/enerjisa.webp' },
      ],

      check:
        'BU BÖLÜMDE İKİ SORU — sayılar ve logolar.\n\n' +
        'SAYILAR. Canlı sitedeki rakamlarla çelişiyor: orada 15+ sektör, 35+ ülke, ' +
        '500+ proje yazıyor. digitopia.co\'yu görmüş biri farkı hemen görür. İki seti ' +
        'uzlaştır ve aynı sayıları her geçtiği yerde kullan — rapor bölümünde, ' +
        'Hakkımızda sayfasında ve meta açıklamasında tekrar ediyorlar.\n\n' +
        'LOGOLAR — altmıştan on altı. Pazarlama klasöründe 60 logo var; bu duvar ilk ' +
        'bakışta okunan ve global ile Türk markalarını dengeleyen on altısını ' +
        'gösteriyor. İzin senin teyidini bekliyor: klasörün adı bu web sitesi için ' +
        'hazırlandığını söylüyor, ama müşteri logosu onların markası — tercihen yazılı ' +
        'alınmalı. (Buradaki dört sorunlu dosyanın hepsi artık değişti: MNG Kargo ve ' +
        'Migros 30.08.2026\'da, a101.webp ile asas.webp ise aynı gün, gri tonlamalı ' +
        'setin A–E yarısı gelince — gerçek bir A101 kırpımı ve ASAŞ markası.)\n\n' +
        'BAKILACAK YENİ BİR ŞEY. O gri tonlamalı set duvardaki dosyaları şirket şirket ' +
        'değiştiriyor ve bu bant da aynı dosyaları çiziyor — yani Bridgestone, DHL, ' +
        'Carlsberg, Anadolu Efes ve Brisa burada artık gri, Siemens, Unilever, ' +
        'Vodafone, Garanti BBVA, ING, TEMSA, Petrol Ofisi, Turkish Airlines ve ' +
        'Enerjisa hâlâ renkli. Setin F–Z yarısı gelince kendiliğinden düzelecek; o ' +
        'zamana kadar bant kazara değil, bilerek karışık.'
    },
    {
      kind: 'cards',
      // İngilizcesiyle aynı: başlık ve spot yok. KEŞFET / SAHAYA AL / SONUÇ
      // kickerları da 28.08.2026'da kaldırıldı; çözüm adları başlığın yerini
      // alıyor ve kart altındaki bağlantı pill buton oldu.
      prominent: true,
      buttonCtas: true,
      // Solda, ortalı değil (01.09.2026: *"bu explore butonu ortalı olmasın
      // yine eski hali solda dursun"*) — İngilizcesiyle aynı.
      ctaAlignLeft: true,
      // İngilizcesiyle aynı: çözüm adları da Explore butonuyla aynı bağlantıyı
      // taşıyor. Görünüm değişmiyor — bağlantı başlığın rengini ve kalınlığını
      // devralıyor, altı çizilmiyor.
      linkTitles: true,
      items: [
        {
          // İngilizcesindeki gibi başlık iki satır: üçüncü kartın adı zaten
          // kendiliğinden sarıyor, bu ikisi sarmayınca kartlar farklı
          // yüksekliklerden başlıyordu.
          title: 'Strateji\nDanışmanlığı',
          promise: 'Ajan tabanlı yapay zeka yolculuğunuzu kurgulayın ve önceliklendirin.',
          points: [
            'Öncelik kavgasını bitirin',
            'Yöneticilerinizi hizalayın',
            'Ortak bir vizyon kurun',
          ],
          cta: { label: 'Keşfedin', href: LINKS.strategy },
        },
        {
          title: 'Dönüşüm\nYönetimi',
          promise: 'Ajan tabanlı yapay zeka yolculuğunuzu güvence altına alın ve hızlandırın.',
          points: [
            'Hedeflenen faydayı güvence altına alın',
            'Uygulamayı hızlandırın',
            'Değişimi yönetin ve sürdürün',
          ],
          cta: { label: 'Keşfedin', href: LINKS.transformation },
        },
        {
          title: 'Kurumsal Yapay Zeka Çözümleri',
          promise: 'Ajan tabanlı yapay zeka çözümlerinizi ölçeklendirerek hayata geçirin.',
          points: [
            'Ajan altyapınızı kurun',
            'Faydayı somut sonuca çevirin',
            'Güvenle ölçeklendirin',
          ],
          cta: { label: 'Keşfedin', href: LINKS.enterpriseAi },
        },
      ],
    },
    // "Güvenli, ölçülebilir ve başarılı dönüşüm." bandı ana sayfadan kaldırıldı
    // (2026-08-28 isteği: *"burayı direkt silelim main page den, gerek yok"*).
    // İngilizce sayfada da kaldırıldı; sıra "Üç giriş yolu"ndan doğrudan müşteri
    // sözlerine geçiyor. Türkçe başlık görseli eksikliğine dair açık check de
    // bandla birlikte gitti — artık ortada band yok.
    {
      // İngilizcesiyle aynı: iki kutucuk yan yana, hall of fame kartının sade
      // hâli (`variant: 'minimal'`) — logo, söz, adın yanında yüz ve kendi
      // vaka çalışmasına giden buton. Diziler bütün hâlinde geçtiği için iki
      // kart da burada yeniden yazılıyor; alıntılar Türkçeleştirildi, isimler
      // ve unvanlar İngilizce sayfadaki gibi.
      kind: 'quotes',
      variant: 'minimal',
      heading: 'Kendi ifadeleriyle.',
      items: [
        {
          company: 'LC Waikiki',
          // See the English page's entry for this card: `inkOffsetY: 0.09`
          // clipped the top of the wordmark, halved here for the same fix.
          logo: {
            name: 'LC Waikiki',
            src: '/logo/lcwaikikicard.png',
            ink: 0.15,
            inkWidth: 0.89,
            inkOffsetY: 0.045,
          },
          portrait: { src: '/photo/portrait-serafettin-ozer.png', alt: 'Şerafettin Özer' },
          quote: [
            'Esnek ve iş birliğine açık yaklaşımları nedeniyle Digitopia’yı büyük danışmanlık '
            + 'şirketlerine ve Gartner’a tercih ettik. Katı, mekanik bir sürecin ötesine geçip '
            + 'bize özel, derinlikli içgörüler sunabilecek bir partnere ihtiyacımız vardı.',
            'En baştan itibaren geri bildirime açık olmaları ve sürekli gelişme çabaları, bu '
            + 'ortaklığın belirleyici unsurları oldu.',
          ],
          source: 'Şerafettin Özer, Chief Digital and Information Officer',
          cta: { label: 'Vaka çalışmasını okuyun', href: '/en/customers/lc-waikiki' },
        },
        {
          company: 'Litehouse Foods',
          logo: {
            name: 'Litehouse Foods',
            src: '/logo/litehousecard.png',
            ink: 0.35,
            inkWidth: 0.57,
            inkScale: 1.3,
          },
          portrait: {
            src: '/photo/portrait-ben-bryan.png',
            alt: 'Ben Bryan, gri ceket ve yakası açık gömlekle gülümserken; siyah beyaz.',
          },
          // Onaylandı (2026-09-01: "LITEHOUSE FOODS diyelim türkçe sayfada") —
          // bu söz hiç Türkçe söylenmemişti, aşağıdaki check bunu zaten
          // öneriyordu; İngilizcesi aynen bırakıldı.
          quote: [
            'At Litehouse Foods, we partnered with Fisher Management Partners and Digitopia to '
            + 'assess and accelerate our AI journey. The AI Maturity Index gave us a clear '
            + 'baseline, aligned our leadership team, and helped translate our AI vision into a '
            + 'practical roadmap for execution. The AIMI deliverables have helped refine my AI '
            + 'message to my management team and business leaders. We now move forward '
            + 'with clarity, governance, and real momentum.',
          ],
          source: 'Ben Bryan, Manager, Applications',
          cta: { label: 'Vaka çalışmasını okuyun', href: '/en/customers/litehouse-foods' },
        },
      ],
      // İngilizcesiyle aynı: ikilinin altındaki buton kaldırıldı (2026-08-29:
      // *"en alttaki Read the hall of fame butonuna gerek yok"*). Her kartın
      // kendi butonu zaten var.
      check:
        'Ben Bryan’ın (Litehouse Foods) sözü onaylandı ve İngilizcesine döndürüldü (2026-09-01). '
        + 'Şerafettin Özer’in sözü hâlâ Türkçeye çevrilmiş hâlde duruyor; o da aslında İngilizce '
        + 'söylenmişti ve yayımlanmadan önce onay ister. İsterseniz bu kart da Türkçe sayfada '
        + 'İngilizce bırakılabilir.',
    },
    {
      // İngilizcesiyle aynı: dört çeyrek profili yerine üç tool, her biri kendi
      // butonuyla. Tool adları İngilizce kalıyor — ürün adları çevrilmiyor.
      kind: 'cards',
      heading: 'Gerçekte ne kadar hazırsınız?',
      sub: 'Üç kısa değerlendirme, üç ayrı soru. Hiçbiri altı dakikadan uzun sürmüyor ve her biri tartışabileceğiniz net bir sonuçla bitiyor.',
      // İngilizcesiyle aynı: 01.09.2026'da CTA'lar butonluktan çıktı, mavi
      // yazı ve yanında ok oldu — yani kart renderer'ının varsayılan CTA'sı.
      // `ruledColumns` sütunlar arasındaki ince çizgileri koruyor; onu
      // `buttonCtas` taşıyordu ve talep çizgilerin kalkmasını istemiyordu.
      ruledColumns: true,
      // İngilizcesiyle aynı: tool adları da altındaki Başla butonuyla aynı
      // bağlantıyı taşıyor (30.08.2026). Görünüm değişmiyor — bağlantı
      // başlığın rengini ve kalınlığını devralıyor, altı çizilmiyor.
      linkTitles: true,
      // İngilizcesiyle aynı: kickerlar 28.08.2026'da kaldırıldı, üç kartın
      // başlığı aynı hizada başlasın diye. CTA'lar da tek kelimeye indi.
      items: [
        {
          title: 'Agentic Readiness Index',
          promise: 'İşinizin ne kadarını gerçekten bir ajana bırakırdınız? Sorunun iki tarafı da puanlanıyor.',
          points: [
            'İşin yetki verdiği ile sistemlerin yapabildiği, ayrı ayrı',
            'Arketipiniz: niyet ağırlıklı mı, kabiliyet ağırlıklı mı',
            'Sizi ilk durduracak olan tek boşluk',
          ],
          cta: { label: 'Başlayın', href: LINKS.readinessTool },
        },
        {
          title: 'Transformation Readiness',
          promise: 'Düşündüğünüz dönüşüm, ilk zorlu çeyreği atlatır mı?',
          points: [
            'Niyet ve kabiliyet iki ayrı eksen, tek bir ortalama değil',
            'Dört çeyrekten hangisinde olduğunuz',
            'İngilizce ve Türkçe',
          ],
          cta: { label: 'Başlayın', href: LINKS.appetiteTool },
        },
        {
          title: 'The Guts Check',
          promise: 'Dört seviyeden hangisindesiniz ve niyetiniz, altındaki en zayıf halkadan ne kadar uzakta?',
          points: [
            'Niyet, sahiplik, uygulama ve kanıt; ayrı ayrı puanlanıyor',
            'Kapsamın sessizce küçülmesini önceden haber veren niyet–gerçeklik boşluğu',
            'Seviyenize özel, önce yapılacak üç şey',
          ],
          cta: { label: 'Başlayın', href: LINKS.gutsCheckTool },
        },
      ],
      check:
        'BU BÖLÜM ARTIK TÜRKÇE OKURU DOĞRUDAN TOOL SAYFALARINA GÖNDERİYOR, ama ' +
        'üçünden ikisinin Türkçesi henüz yazılmadı: Agentic Readiness Index ve ' +
        'The Guts Check İngilizce çalışıyor (Transformation Readiness iki dilli ' +
        'geldi). Eskiden bu bölüm bir açıklamaydı, tıklanacak üç buton değildi — ' +
        'yani dil farkı şimdi daha çok görülecek. Halil\'in 1 Eylül öncesi kararı: ' +
        'ya iki tool çevrilir, ya Türkçe lansmanda İngilizce kalacakları kabul ' +
        'edilir. Karar tool sayfalarındaki notlarla aynı, burada tekrar edilmesinin ' +
        'sebebi ana sayfanın artık o kapıyı açıyor olması.',
    },
    {
      // İngilizce sayfadaki gibi: bu üç bölüm — oda, çark ve rapor — birbirinden
      // kopuk üç görsel olarak okunuyordu. Üçü de artık aynı argümandaki yerini
      // söyleyen birer cümle taşıyor.
      kind: 'photo',
      photo: {
        src: '/photo/office-digital-vitality.jpg',
        alt: 'Bir Digitopia danışmanı, aydınlık bir toplantı odasında üç yöneticiye Digital Vitality çeyreklerini, yani dayanıklılığa karşı hızı anlatıyor.',
      },
      caption:
        'Hangi değerlendirmeyi çözerseniz çözün sonu aynı: ekranda bir skor ve o skora göre hareket edecek insanların o skoru tartışması.',
    },
    {
      // İngilizce sayfadaki figure'ın Türkçesi. Gönderilen export bu sayfaya
      // konuluyor: boyut adları ve alt başlıkları Türkçe basılmış, ortadaki
      // kilit de Türkçe — yani strategy-consulting'in kullandığı İngilizce
      // çarkın burada okunması gerekmiyor.
      kind: 'figure',
      photo: {
        src: '/photo/daimi-framework-wheel-turkce-labelled.png',
        alt: 'DAIMI çarkı: Dijital ve Yapay Zeka Olgunluk Endeksi’nin çevresinde Müşteri, Operasyon, İnovasyon, İnsan, Yönetişim ve Teknoloji; her birinin altında ölçülen alt boyutlarla.',
      },
      caption:
        'Üç değerlendirme de tek bir modele göre puanlanır: altı DAIMI boyutu ve her birinin altında ölçülen alt boyutlar.',
      // 31.08.2026 isteğiyle tıklanabilir yapıldı — DAIMI'nin kendi sayfasına
      // götürüyor, görünümde hiçbir değişiklik yok.
      href: LINKS.daimi,
    },
    {
      kind: 'cta',
      heading: 'Dijital ve Yapay Zeka Olgunluk Raporu 2026',
      sub: 'Sektörünüz aynı altı boyutta nerede duruyor ve üst çeyreği diğerlerinden ayıran ne? Anket paneliyle değil, tamamlanmış değerlendirmelerle kuruldu. 3.000+ ölçüm, 40+ ülke, 20+ sektör.',
      tone: 'band',
      photo: {
        src: '/photo/daimi-maturity-index-gap.webp',
        alt: 'Olgunluk Endeksi, yıllara göre: DMI 2020’de 2.70’ten 2025’te 3.06’ya, DAIMI 2025’te 2.93, AIMI 2024’te 2.30 ve 2025’te 2.38',
      },
      // 31.08.2026 isteğiyle tıklanabilir yapıldı — bu grafiğin kaynağı olan
      // rapora götürüyor, görünümde hiçbir değişiklik yok.
      photoHref: LINKS.daimiReport,
      // 08.09.2026 isteğiyle başlık da tıklanabilir — yanındaki grafik ve
      // butonla aynı hedef, görünümde hiçbir değişiklik yok.
      headingHref: LINKS.daimiReport,
      ctas: [
        // 08.09.2026 isteğiyle Raporlar listesi yerine doğrudan raporun kendi
        // sayfasına gidiyor; yanındaki grafikle aynı hedef.
        { label: '2026 raporunu indirin', href: LINKS.daimiReport },
        { label: 'Yapay zeka hazırlığınızı ölçün', href: LINKS.readinessTool },
      ],
    },
    {
      kind: 'form',
      // 30.08.2026 isteğiyle Türkçesi yazıldı: hero'daki micro satırla aynı üç
      // kelime, aynı sırada. Hashtag İngilizce kalıyor — o bir marka etiketi,
      // cümle değil.
      heading: 'KEŞFET. HAYATA GEÇİR. SONUÇ AL.',
      sub: '#SCALEVALUE',
      fields: [
        { name: 'name', label: 'Ad soyad', type: 'text', required: true },
        { name: 'email', label: 'Kurumsal e-posta', type: 'email', required: true },
        { name: 'company', label: 'Şirket', type: 'text', required: true },
        // Ülke adları Türkçe ve Türkçe alfabetik sırada — ürün adlarının aksine
        // ülke adı teknik terim değil, ve "Türkiye"yi T'de arayan biri onu
        // İngilizce sıradaki yerinde bulamaz.
        {
          name: 'country',
          label: 'Ülke',
          type: 'select',
          placeholder: 'Ülke seçin',
          options: COUNTRIES_TR,
        },
        { name: 'goal', label: 'Mesaj', type: 'textarea' },
      ],
      consent: 'Digitopia\'nın talebimle ilgili benimle iletişime geçmesini kabul ediyorum.',
      submit: 'Gönder',
      confirmation:
        'Teşekkürler. Her mesajı okuyoruz. İki iş günü içinde bir uygulama liderinden yanıt bekleyin.',
      // İngilizce bölümdeki `check` bu sayfada görünmüyor — `sections` atomik bir
      // array, yani /tr kendi dizisini veriyor ve İngilizce'nin notunu miras
      // almıyor. 2026-08-27'de soru Türkçe geldiği için not da Türkçe duruyor;
      // İngilizce karşılığı `content/pages/home.ts` içinde.
      check:
        'İki iş günü sözünü yayına almadan önce teyit edin — sitedeki en kolay ' +
        'kırılacak taahhüt bu ve prospect\'in fark edeceği tek şey. Tutamıyorsanız ' +
        'değiştirin ya da süreyi kaldırın. ' +
        'Ayrıca: bu form da Contact Us sayfasındaki form da hiçbir yere submit ' +
        'etmiyor. Gönder butonu bilinçli olarak disabled — çalışıyormuş gibi ' +
        'görünüp gelen talebi sessizce kaybeden bir form, açıkça çalışmayan bir ' +
        'formdan daha kötü olurdu. digitopia.co\'daki iki workflow da burada yok: ' +
        'ne info@m.digitopia.co\'dan giden bilgilendirme maili, ne de talebin ' +
        'Türkiye\'den geldiğinde halil + feyza, dışından geldiğinde halil + ' +
        'fatih.eryigit adreslerine düşmesi. Bunlar formun post edildiği yerde ' +
        'kurulan workflow\'lar, site içeriği değil. Sitedeki diğer bütün lead ' +
        'path\'leri HubSpot form (portal 6887774); eksik olan tek şey contact ' +
        'formunun HubSpot form id\'si — o gelince embed buraya takılır ve ' +
        'routing HubSpot tarafında tanımlanır.',
    },
  ],
}
